import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { useLanguage } from '@/contexts/LanguageContext';
import { fallanalyseAPI } from '@/lib/api';
import { toast } from 'sonner';
import { StepEingabe } from '@/components/fallanalyse/StepEingabe';
import { StepErkannt } from '@/components/fallanalyse/StepErkannt';
import { StepEinschaetzung } from '@/components/fallanalyse/StepEinschaetzung';
import { StepWeiter } from '@/components/fallanalyse/StepWeiter';
import { StepAssistent } from '@/components/fallanalyse/StepAssistent';
import { EingabeKontext } from '@/components/fallanalyse/EingabeKontext';

const STEP_KEYS = ['schnellcheck.step1', 'schnellcheck.step2', 'schnellcheck.step3', 'schnellcheck.step4'];

export default function FallanalysePage() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [step, setStep] = useState(1);
  const [freitext, setFreitext] = useState('');
  const [loading, setLoading] = useState(false);
  const [ansicht, setAnsicht] = useState(null);
  const [plausibilitaet, setPlausibilitaet] = useState(null);
  const [zielgruppe, setZielgruppe] = useState('waldbesitzer');

  const analyseStarten = async (text) => {
    if (!text || text.trim().length < 10) {
      toast.error(t('schnellcheck.inputTooShort'));
      return;
    }
    setLoading(true);
    setPlausibilitaet(null);
    try {
      const res = await fallanalyseAPI.nutzeransicht(text.trim(), null, zielgruppe);
      if (res.data.status && res.data.status !== 'ok') {
        setPlausibilitaet(res.data.plausibilitaet);
        setAnsicht(null);
        setStep(1);
      } else {
        setAnsicht(res.data);
        setPlausibilitaet(null);
        setStep(2);
      }
    } catch {
      toast.error(t('schnellcheck.analyseFehler'));
    } finally {
      setLoading(false);
    }
  };

  const ergaenzenUndNeuAnalysieren = async (zusatzText) => {
    const neuerText = freitext + '\n' + zusatzText;
    setFreitext(neuerText);
    await analyseStarten(neuerText);
  };

  const neuStarten = () => {
    setStep(1);
    setFreitext('');
    setAnsicht(null);
    setPlausibilitaet(null);
    setZielgruppe('waldbesitzer');
  };

  return (
    <AppShell>
      <div className="max-w-3xl mx-auto" data-testid="fallanalyse-page">
        {/* Stepper */}
        <nav className="mb-10" aria-label="Fortschritt" data-testid="fallanalyse-stepper">
          <ol className="flex items-center gap-0">
            {STEP_KEYS.map((key, i) => {
              const nr = i + 1;
              const done = step > nr;
              const active = step === nr;
              return (
                <li key={nr} className="flex items-center flex-1 last:flex-none">
                  <button
                    disabled={nr > step}
                    onClick={() => nr <= step && setStep(nr)}
                    className={`flex items-center gap-2 text-sm font-medium transition-colors ${
                      active ? 'text-primary' : done ? 'text-primary/60 hover:text-primary' : 'text-gray-400'
                    }`}
                    data-testid={`step-${nr}-btn`}
                  >
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all ${
                      active ? 'bg-primary text-white border-primary' :
                      done ? 'bg-primary/10 text-primary border-primary/30' :
                      'bg-gray-100 text-gray-400 border-gray-200'
                    }`}>
                      {done ? '\u2713' : nr}
                    </span>
                    <span className="hidden sm:inline">{t(key)}</span>
                  </button>
                  {i < STEP_KEYS.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-3 rounded ${done ? 'bg-primary/30' : 'bg-gray-200'}`} />
                  )}
                </li>
              );
            })}
          </ol>
        </nav>

        {/* EingabeKontext – sichtbar in Steps 2-4 */}
        {step >= 2 && ansicht && (
          <EingabeKontext
            freitext={freitext}
            zielgruppe={{ key: zielgruppe, label: zielgruppe }}
            onFreitextAendern={(text) => { setFreitext(text); analyseStarten(text); }}
            loading={loading}
            t={t}
          />
        )}

        {/* Step Content */}
        {step === 1 && !plausibilitaet && (
          <StepEingabe
            freitext={freitext}
            setFreitext={setFreitext}
            onSubmit={analyseStarten}
            loading={loading}
            isPublic={false}
            zielgruppe={zielgruppe}
            onZielgruppeChange={setZielgruppe}
            t={t}
          />
        )}
        {step === 1 && plausibilitaet && (
          <StepAssistent
            ansicht={{ assistenz: plausibilitaet }}
            onErgaenzen={(text) => { setFreitext(text); analyseStarten(text); }}
            onZurueck={() => setPlausibilitaet(null)}
            loading={loading}
            t={t}
          />
        )}
        {step === 2 && ansicht && (
          <StepErkannt
            ansicht={ansicht}
            onWeiter={() => setStep(3)}
            onErgaenzen={ergaenzenUndNeuAnalysieren}
            onZurueck={() => setStep(1)}
            loading={loading}
            t={t}
          />
        )}
        {step === 3 && ansicht && (
          <StepEinschaetzung
            ansicht={ansicht}
            onWeiter={() => setStep(4)}
            onZurueck={() => setStep(2)}
            t={t}
          />
        )}
        {step === 4 && ansicht && (
          <StepWeiter
            ansicht={ansicht}
            onNeustart={neuStarten}
            onProjektErstellen={() => navigate('/projekte/neu')}
            isPublic={false}
            onZurueck={() => setStep(3)}
            t={t}
          />
        )}
      </div>
    </AppShell>
  );
}
